import { apiService } from '@shared/services/apiServices';
import { AxiosError } from 'axios';
import { CERTIFICATE_ERROR_MESSAGES } from '../errors/errorMessages';
import type {
  CreateCertificateTemplatePayload,
  CreateCertificateTemplateApiResponse,
} from '../types';
import certificateTemplateEndpoints from './certificateTemplateEndpoints';

// ─── Error helpers ────────────────────────────────────────────────────────────

function extractErrorMessage(errorData: unknown, status?: number): string {
  if (status === 401 || status === 403) {
    return CERTIFICATE_ERROR_MESSAGES.UNAUTHORIZED;
  }
  if (status === 500) {
    return CERTIFICATE_ERROR_MESSAGES.INTERNAL_SERVER_ERROR;
  }
  if (status === 400) {
    if (errorData && typeof errorData === 'object' && 'message' in errorData) {
      return (errorData as { message: string }).message;
    }
    return CERTIFICATE_ERROR_MESSAGES.VALIDATION_ERROR;
  }
  if (errorData && typeof errorData === 'object' && 'message' in errorData) {
    return (errorData as { message: string }).message;
  }
  return CERTIFICATE_ERROR_MESSAGES.DEFAULT_ERROR;
}

// ─── Public API ───────────────────────────────────────────────────────────────

export interface CreateCertificateTemplateResult {
  success: boolean;
  data?: CreateCertificateTemplateApiResponse;
  error?: string;
}

/**
 * Creates a new certificate template via the backend API.
 *
 * @param payload - The validated certificate template data to persist.
 * @returns A result object indicating success/failure and the created template
 *          data (on success) or an error message (on failure).
 */
export async function createCertificateTemplate(
  payload: CreateCertificateTemplatePayload
): Promise<CreateCertificateTemplateResult> {
  try {
    const result = await apiService.postAPI(
      certificateTemplateEndpoints.CREATE,
      payload
    );

    if (!result.error) {
      return {
        success: true,
        data: result.data as CreateCertificateTemplateApiResponse,
      };
    }

    if (result.isNetworkError) {
      return {
        success: false,
        error: CERTIFICATE_ERROR_MESSAGES.NETWORK_ERROR,
      };
    }

    return {
      success: false,
      error: extractErrorMessage(result.data, result.status),
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      const isNetworkError = !error.response && !!error.request;
      if (isNetworkError) {
        return {
          success: false,
          error: CERTIFICATE_ERROR_MESSAGES.NETWORK_ERROR,
        };
      }
      return {
        success: false,
        error: extractErrorMessage(
          error.response?.data,
          error.response?.status
        ),
      };
    }
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : CERTIFICATE_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
}
